import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Modal,
  ModalBody,
  Input,
  Label,
  FormGroup,
  Form,
  FormFeedback,
} from "reactstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { isNumberOnly } from "helpers/GeneralHelper";
import {
  getCommonTaskBucketGroupOptions,
  submitTaskGroupSettingsFilterData,
  updateTaskGroupSettings,
} from "model/TaskGroupSettingsModel";

const BOARD_LAYOUT_OPTIONS = [
  { label: "Matrix", value: "matrix" },
  { label: "Flat", value: "flat" },
];

const INITIAL_FORM_STATE = {
  groupId: "",
  overdueAfterHours: "",
  boardLayout: "",
};

const taskGroupSettingsSchema = Yup.object().shape({
  groupId: Yup.string().trim().required("Group is required"),
  overdueAfterHours: Yup.string()
    .trim()
    .required("Overdue After Hours is required")
    .test("is-number", "Overdue After Hours must be a number", (value) =>
      isNumberOnly(value)
    ),
  boardLayout: Yup.string()
    .oneOf(["matrix", "flat"], "Board Layout is required")
    .required("Board Layout is required"),
});

const mapCommonOptions = (payload) => {
  const list = payload?.data ?? payload ?? [];
  if (!Array.isArray(list)) return [];
  return list
    .map((item) => {
      const value = item?.id ?? item?.value ?? item?.groupId;
      if (value == null || value === "") return null;
      const label =
        String(item?.display ?? item?.title ?? item?.name ?? item?.label ?? value).trim() ||
        String(value);
      return { value: String(value), label };
    })
    .filter(Boolean);
};

const resolveFilterRows = (payload) => {
  const raw = payload?.data ?? payload;
  const list = Array.isArray(raw) ? raw : raw?.records ?? raw?.rows ?? [];
  return Array.isArray(list) ? list : [];
};

const findRowByGroupId = (rows, groupId) => {
  const targetId = String(groupId ?? "").trim();
  if (!targetId) return undefined;
  return rows.find((row) => {
    const rowGroupId =
      row?.groupId ??
      row?.group?.id ??
      row?.taskBucketGroupId ??
      row?.TaskBucketGroupId ??
      row?.id;
    return String(rowGroupId ?? "") === targetId;
  });
};

const buildFormDataFromRow = (row) => ({
  groupId: String(
    row?.groupId ??
      row?.group?.id ??
      row?.taskBucketGroupId ??
      row?.TaskBucketGroupId ??
      row?.id ??
      ""
  ),
  overdueAfterHours:
    row?.overdueAfterHours != null ? String(row.overdueAfterHours) : "",
  boardLayout: row?.boardLayout ? String(row.boardLayout) : "",
});

const TaskGroupAddEditModal = ({
  isOpen,
  onClose,
  groupId,
  editRow = null,
  onSuccess,
}) => {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [formKey, setFormKey] = useState(0);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [groupOptions, setGroupOptions] = useState([]);
  const [groupLoading, setGroupLoading] = useState(false);

  const isEditMode = Boolean(groupId);

  const resetForm = () => {
    setFormData(INITIAL_FORM_STATE);
    setErrors({});
    setFormKey((k) => k + 1);
  };

  const closeModal = () => {
    resetForm();
    onClose?.();
  };

  const loadGroupOptions = async () => {
    setGroupLoading(true);
    const response = await getCommonTaskBucketGroupOptions();
    setGroupOptions(mapCommonOptions(response));
    setGroupLoading(false);
  };

  useEffect(() => {
    if (isOpen) {
      loadGroupOptions();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const loadFormData = async () => {
      if (!groupId) {
        resetForm();
        return;
      }

      const editRowGroupId = String(
        editRow?.groupId ??
          editRow?.group?.id ??
          editRow?.taskBucketGroupId ??
          editRow?.TaskBucketGroupId ??
          editRow?.id ??
          ""
      ).trim();

      if (editRow && editRowGroupId === String(groupId)) {
        setFormData(buildFormDataFromRow(editRow));
        setErrors({});
        setFormKey((k) => k + 1);
        return;
      }

      const response = await submitTaskGroupSettingsFilterData({});
      const row = findRowByGroupId(resolveFilterRows(response), groupId);
      if (row) {
        setFormData(buildFormDataFromRow(row));
      } else {
        setFormData({
          ...INITIAL_FORM_STATE,
          groupId: String(groupId),
        });
      }
      setErrors({});
      setFormKey((k) => k + 1);
    };

    loadFormData();
  }, [isOpen, groupId, editRow]);

  const selectedGroupOption = useMemo(
    () =>
      groupOptions.find(
        (option) => String(option.value) === String(formData.groupId || "")
      ) || null,
    [groupOptions, formData.groupId]
  );

  const selectedBoardLayoutOption = useMemo(
    () =>
      BOARD_LAYOUT_OPTIONS.find(
        (option) => String(option.value) === String(formData.boardLayout || "")
      ) || null,
    [formData.boardLayout]
  );

  const handleOverdueChange = async (e) => {
    const { value } = e.target;
    if (value !== "" && !isNumberOnly(value)) return;

    setFormData((prev) => ({
      ...prev,
      overdueAfterHours: value,
    }));

    try {
      await Yup.reach(taskGroupSettingsSchema, "overdueAfterHours").validate(value);
      setErrors((prev) => ({ ...prev, overdueAfterHours: "" }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, overdueAfterHours: err.message }));
    }
  };

  const handleGroupChange = async (selectedOption) => {
    const value = selectedOption?.value ? String(selectedOption.value) : "";
    setFormData((prev) => ({ ...prev, groupId: value }));

    try {
      await Yup.reach(taskGroupSettingsSchema, "groupId").validate(value);
      setErrors((prev) => ({ ...prev, groupId: "" }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, groupId: err.message }));
    }
  };

  const handleBoardLayoutChange = async (selectedOption) => {
    const value = selectedOption?.value ? String(selectedOption.value) : "";
    setFormData((prev) => ({ ...prev, boardLayout: value }));

    try {
      await Yup.reach(taskGroupSettingsSchema, "boardLayout").validate(value);
      setErrors((prev) => ({ ...prev, boardLayout: "" }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, boardLayout: err.message }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await taskGroupSettingsSchema.validate(formData, { abortEarly: false });
      setErrors({});
    } catch (err) {
      const newErrors = {};
      err.inner.forEach((error) => {
        newErrors[error.path] = error.message;
      });
      setErrors(newErrors);
      return;
    }

    const payload = {
      overdueAfterHours: Number(formData.overdueAfterHours),
      boardLayout: formData.boardLayout,
    };

    setSubmitting(true);
    try {
      const response = await updateTaskGroupSettings(formData.groupId, payload);
      if (response?.success) {
        toast.success(response?.message);
        closeModal();
        onSuccess?.();
      } else {
        toast.error(response?.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={closeModal}
      backdrop="static"
      className="modal-full-height"
      centered
      zIndex={5000}
    >
      <ModalBody
        key={formKey}
        className="p-4"
        style={{ maxHeight: "95vh", overflowY: "auto" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="fw-bold mb-0">
            {isEditMode ? "Edit" : "Add"} Task Group Settings
          </h5>
          <Button close onClick={closeModal} />
        </div>
        <hr />

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>
              Group Id <span className="text-danger">*</span>
            </Label>
            <Select
              isLoading={groupLoading}
              isDisabled={isEditMode}
              options={groupOptions}
              className={`select2-selection${errors.groupId ? " is-invalid" : ""}`}
              placeholder="Select Group"
              value={selectedGroupOption}
              onChange={handleGroupChange}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
            {errors.groupId && (
              <FormFeedback className="d-block">{errors.groupId}</FormFeedback>
            )}
          </FormGroup>

          <FormGroup>
            <Label>
              Overdue After Hours <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              name="overdueAfterHours"
              value={formData.overdueAfterHours}
              onChange={handleOverdueChange}
              invalid={!!errors.overdueAfterHours}
            />
            <FormFeedback>{errors.overdueAfterHours}</FormFeedback>
          </FormGroup>

          <FormGroup>
            <Label>
              Board Layout <span className="text-danger">*</span>
            </Label>
            <Select
              options={BOARD_LAYOUT_OPTIONS}
              className={`select2-selection${errors.boardLayout ? " is-invalid" : ""}`}
              placeholder="Select Board Layout"
              value={selectedBoardLayoutOption}
              onChange={handleBoardLayoutChange}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
            {errors.boardLayout && (
              <FormFeedback className="d-block">{errors.boardLayout}</FormFeedback>
            )}
          </FormGroup>

          <div className="text-end mt-3">
            <Button color="primary" type="submit" disabled={submitting}>
              {isEditMode ? "Update" : "Save"}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default TaskGroupAddEditModal;
